import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer, staggerItem, floatingIconVariants, cardHover } from '../../utility/framerMotionAnimations';

const AnchorAboutSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const sectionRef = useRef(null);

    // Intersection Observer to trigger animation when section is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true);
                    startAnimationSequence();
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, [isVisible]);

    const startAnimationSequence = () => {
        // Step 1: Show Drive Growth card
        setTimeout(() => {
            setCurrentStep(1);
        }, 500);

        // Step 2: Show Faster Payments card
        setTimeout(() => {
            setCurrentStep(2);
        }, 1000);

        // Step 3: Show Build Trust card
        setTimeout(() => {
            setCurrentStep(3);
        }, 1500);
    };

    return (
        <div className="anchor-about-section" ref={sectionRef}>
            <div className="anchor-about-section-container">
                <motion.div 
                    className="anchor-about-section-header"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                    variants={fadeInUp}
                >
                    <motion.h2 
                        className="anchor-about-section-title"
                        variants={fadeInUp}
                    >
                        Improve your CashFlow with Anchor Based Financing'
                    </motion.h2>
                </motion.div>

                <motion.div 
                    className="anchor-about-section-content"
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                >
                    {/* Drive Growth Card */}
                    <motion.div 
                        className={`anchor-about-section-card ${currentStep >= 1 ? 'anchor-about-section-card-visible' : ''}`}
                        variants={staggerItem}
                        whileHover={cardHover.hover}
                    >
                        <motion.div 
                            className="anchor-about-section-card-icon"
                            variants={floatingIconVariants}
                            animate="animate"
                            style={{ background: 'transparent', backgroundColor: 'transparent' }}
                        >
                            <img src="/images/drive-growth.png" alt="Drive Growth" />
                        </motion.div>
                        <div className="anchor-about-section-card-title">Drive Growth</div>
                        <div className="anchor-about-section-card-description">
                            Credit will help your suppliers grow with easy funds availability.
                        </div>
                    </motion.div>

                    {/* Faster Payments Card */}
                    <motion.div 
                        className={`anchor-about-section-card ${currentStep >= 2 ? 'anchor-about-section-card-visible' : ''}`}
                        variants={staggerItem}
                        whileHover={cardHover.hover}
                    >
                        <motion.div 
                            className="anchor-about-section-card-icon"
                            variants={floatingIconVariants}
                            animate="animate"
                            style={{ background: 'transparent', backgroundColor: 'transparent' }}
                        >
                            <img src="/images/faster-payments.webp" alt="Faster Payments" />
                        </motion.div>
                        <div className="anchor-about-section-card-title">Faster Payments</div>
                        <div className="anchor-about-section-card-description">
                            Bring down vendors pay out cycle to up to 1 day.
                        </div>
                    </motion.div>

                    {/* Build Trust Card */}
                    <motion.div 
                        className={`anchor-about-section-card ${currentStep >= 3 ? 'anchor-about-section-card-visible' : ''}`}
                        variants={staggerItem}
                        whileHover={cardHover.hover}
                    >
                        <motion.div 
                            className="anchor-about-section-card-icon"
                            variants={floatingIconVariants}
                            animate="animate"
                            style={{ background: 'transparent', backgroundColor: 'transparent' }}
                        >
                            <img src="/images/build-trust.webp" alt="Build Trust" />
                        </motion.div>
                        <div className="anchor-about-section-card-title">Build Trust</div>
                        <div className="anchor-about-section-card-description">
                            Improve partners stickiness with easy funding access.
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default AnchorAboutSection;

